const COURSE_VARIANT = '64954436878713';

export function isSingleCourseCart(cart, productId, variantId = COURSE_VARIANT) {
  if (!cart || !Array.isArray(cart.items) || cart.items.length !== 1 || cart.item_count !== 1) return false;
  const item = cart.items[0];
  return String(item.product_id) === String(productId) && String(item.variant_id) === variantId && item.quantity === 1;
}

export async function prepareCourseCart({ root, productId, variantId, mode = 'book', fetcher = fetch, signal }) {
  if (variantId !== COURSE_VARIANT || !/^\/[a-zA-Z0-9/_-]*$/.test(root) || root.startsWith('//') || !root.endsWith('/')) throw new Error('CONFIG');
  const request = async (path, body) => {
    const response = await fetcher(root + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', mode: 'same-origin', redirect: 'error', cache: 'no-store', signal,
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw new Error('REQUEST');
    return response.json();
  };
  let cart = await request('cart.js');
  if (isSingleCourseCart(cart, productId, variantId)) return cart;
  if (!Array.isArray(cart.items) || cart.items.length !== 0 || cart.item_count !== 0 || mode !== 'book') throw new Error('CONFLICT');
  try { await request('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] }); }
  catch (error) { if (signal?.aborted) throw error; }
  cart = await request('cart.js');
  if (!isSingleCourseCart(cart, productId, variantId)) throw new Error('CONFLICT');
  return cart;
}

export function bindCourseBooking(form, doc = document, win = window) {
  if (form.dataset.pvoBound === 'true') return;
  form.dataset.pvoBound = 'true';
  const button = form.querySelector('[data-pvo-checkout]');
  const status = form.querySelector('[data-pvo-status]');
  const cartLink = form.querySelector('[data-pvo-cart-link]');
  const checks = [...form.querySelectorAll('input[type="checkbox"][required]')];
  if (!button || !status || checks.length !== 2) return;
  let busy = false;
  let permitNative = false;
  let controller;
  let generation = 0;
  const eligible = () => doc.documentElement.dataset.pvCourseCustomerState === 'eligible';
  const setStatus = (text, isError = false) => { status.textContent = text; status.dataset.error = String(isError); };
  const syncCustomer = () => {
    button.disabled = busy || !eligible();
    const state = doc.documentElement.dataset.pvCourseCustomerState;
    if (!eligible()) setStatus(state === 'owned' ? form.dataset.owned : form.dataset.pending);
    else if (!busy && (status.textContent === form.dataset.owned || status.textContent === form.dataset.pending)) setStatus('');
  };
  doc.addEventListener('pv:course-customer-state', syncCustomer);
  form.addEventListener('submit', async event => {
    if (permitNative && event.submitter === button && eligible() && checks.every(input => input.checked) && form.checkValidity()) {
      permitNative = false;
      return;
    }
    event.preventDefault();
    if (busy || !eligible() || !form.reportValidity() || !checks.every(input => input.checked)) { syncCustomer(); return; }
    if (event.submitter !== button) return;
    busy = true;
    button.disabled = true;
    cartLink.hidden = true;
    setStatus(form.dataset.busy);
    const run = ++generation;
    controller = new AbortController();
    const timer = win.setTimeout(() => controller.abort(), 20000);
    try {
      await prepareCourseCart({ root: form.dataset.cartRoot, productId: form.dataset.productId, variantId: form.dataset.variantId, mode: form.dataset.mode, fetcher: win.fetch.bind(win), signal: controller.signal });
      if (run !== generation) return;
      if (!eligible() || !checks.every(input => input.checked) || !form.reportValidity()) { setStatus(''); return; }
      busy = false;
      button.disabled = false;
      permitNative = true;
      try { form.requestSubmit(button); } finally { permitNative = false; }
    } catch (error) {
      if (run !== generation) return;
      setStatus(error.message === 'CONFLICT' ? form.dataset.conflict : form.dataset.error, true);
      cartLink.hidden = false;
    } finally {
      win.clearTimeout(timer);
      if (run === generation) { busy = false; syncCustomer(); }
    }
  });
  win.addEventListener('pagehide', () => { generation += 1; controller?.abort(); permitNative = false; });
  win.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    busy = false; permitNative = false;
    checks.forEach(input => { input.checked = false; });
    setStatus(''); syncCustomer();
  });
  syncCustomer();
}
if (typeof document !== 'undefined') document.querySelectorAll('[data-pvo-booking]').forEach(form => bindCourseBooking(form));
